import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ISchool } from '../../../models/schools.model';
import { doGetSchools } from '../../../store/school.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import qs from 'qs';
import { useSnackbar } from 'notistack';
import { Table, TableColumnsType } from 'antd';
import Loader from '../../../components/Loader';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Pagination from '../../../components/Pagination';
import Footer from '../../../components/layout/Footer';
import Button from '../../../components/Button';
import config from '../../../lib/config';
import { errorVariant, warningVariant } from '../../../lib/barVariant';
import useAuth from '../../../hooks/useAuth';
import ConfirmModal from '../../../components/modals/ConfirmModal';
import useAxios from '../../../hooks/useAxios';
import { deleteSchool } from '../../../api/schools.api';
import useNotification from '../../../hooks/useNotification';
import ErrorResponse from '../../../components/ErrorResponse';
import SchoolTypeSelect from '../../../components/common/SchoolTypeSelect';

const SystemSchool: FC = () => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const query = qs.parse(location.search, { ignoreQueryPrefix: true });
  const { enqueueSnackbar } = useSnackbar();
  const { successNotification } = useNotification();
  const { commonCodes } = useSelector((state: RootState) => state.commonCode);
  // SY0013 = 학교 구분
  const SY0013 = commonCodes.find((item) => item.commonCode === 'SY0013')?.detailCode;
  const { schools, isLoading } = useSelector((state: RootState) => state.school);
  const { data: deleteRes, isLoading: deleteIsLoading, error: deleteError, execute: deleteExecute } = useAxios();

  const [data, setData] = useState<ISchool[]>([]);
  const [params, setParams] = useState({});
  const [page, setPage] = useState<number>(query.page? Number(query.page) : 1);
  const [schoolGbn, setSchoolGbn] = useState('');
  const [schoolName, setSchoolName] = useState('');
  const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
  const [schoolCode, setSchoolCode] = useState<any>();

  useEffect(() => {
    setSchoolGbn('');
    getSchools();
  }, []);

  useEffect(() => {
    const newParams: any = {};
    if (query.page) {
      newParams.page = Number(query.page);
      setPage(newParams.page);
    }
    if (query.schoolGbn) {
      newParams.schoolGbn = String(query.schoolGbn);
      setSchoolGbn(newParams.schoolGbn);
    }
    if (query.schoolName) {
      newParams.schoolName = String(query.schoolName);
      setSchoolName(newParams.schoolName);
    }
    setParams(newParams);

    let newData: ISchool[] = schools || [];
    if (query.schoolGbn && !query.schoolName) {
      newData = newData.filter(item => item.schoolDiv === query.schoolGbn);
    }
    if (query.schoolName) {
      newData = newData.filter(item => item.schoolName.includes(String(query.schoolName)));
    }
    setData(newData);
  }, [location, schools]);

  useEffect(() => {
    if (deleteRes) {
      getSchools();
      setConfirmDeleteVisible(false);
      successNotification(t('common.message.delete'));
    }
  }, [deleteRes]);

  const getSchools = async () => {
    const res = await dispatch(doGetSchools());

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      case 404:
        enqueueSnackbar(t('common.message.notFound'), warningVariant);
        break;
      default:
        enqueueSnackbar(
          res?.data?.data || t('common.message.error'),
          errorVariant,
        );
    }
  };

  const onSearch = () => {
    const newParams: any = { page: 1 };

    if (schoolGbn) {
      newParams.schoolGbn = schoolGbn;
    }
    if (schoolName) {
      newParams.schoolName = schoolName;
    }

    history.push(`/system/school?${qs.stringify(newParams)}`);
  };

  const getSchoolTypeLabel = (value: string) => {
    const type = SY0013?.find((item: any) => item.detailCode === value);
    return type ? type.korName : value;
  };

  const onDelete = () => {
    if (!schoolCode) return;
    deleteExecute(deleteSchool(schoolCode));
  };

  const columns: TableColumnsType<ISchool> = [
    {
      title: t('system.school.column1'),
      dataIndex: 'schoolCode',
    },
    {
      title: t('system.school.column2'),
      dataIndex: 'schoolName',
      align: 'center',
      render: value => {
        return (<div className='text-left ant-table-cell-ellipsis'>{value}</div>);
      }
    },
    {
      title: t('system.school.column3'),
      dataIndex: 'schoolDiv',
      render: (value: string) => getSchoolTypeLabel(value),
      align: 'center',
    },
    {
      title: t('system.school.column4'),
      dataIndex: 'address',
      render: (_, record) => {
        return (<div className='text-left ant-table-cell-ellipsis'>{`${record.address} ${record.detailAddress || ''}`}</div>);
      },
      align: 'center',
    },
    {
      title: '',
      key: 'menuCode',
      dataIndex: 'menuCode',
      render: (_, record) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small gray"
            text={t('common.button.delete')}
            writeAuthYn={writeAuthYn}
            onClickCapture={(e) => {
              e.stopPropagation();
              setSchoolCode(record.schoolCode);
              setConfirmDeleteVisible(true);
            }}
          />
        </div>
      ),
    },
  ];

  return (
    <>
    <SimpleHeader
      iconFile="icon-system-big.svg"
      parentMenu={t('common.menu.system')}
      currentMenu={t('system.school.title1')}
      onlyTitle={true}
    />

    <div className="common-search">
      <div className="search-area">
        <ul className="search-bx">
          <li>
            <label>{t('statistics.homework.column4')}</label>
            <div>
              <SchoolTypeSelect
                schoolGbn={schoolGbn}
                setSchoolGbn={setSchoolGbn}
              />
            </div>
            <div>
              <input
                type="text"
                className="form-control"
                placeholder={t('system.school.placeholder')}
                onChange={e => setSchoolName(e.target.value)}
                onKeyPress={e => {
                  if (e.key === 'Enter') onSearch();
                }}
              />
            </div>
            <div className="comm-btn__area">
              <Button
                className="btn-basic small black"
                onClick={onSearch}
                text={t('common.button.search')}
              />
            </div>
          </li>
        </ul>
      </div>
    </div>

    <div className="content scrollbar">
      {(isLoading || deleteIsLoading) && <Loader />}
      <ErrorResponse error={deleteError} />
      
      <div className="col-frame">
        <div className="comm-bx__between">
          <div className="comm-sch_result">
            {t('common.text.count')}: <b>{data.length}</b>
            {t('common.text.unit')}
          </div>
          <div className="comm-btn__area">
            {
              <Button
                className="btn-basic pink"
                text={t('common.button.create')}
                onClick={() => {
                  history.push(`/system/school/create?${qs.stringify(params)}`);                  
                }}
                writeAuthYn={writeAuthYn}
              />
            }
          </div>
        </div>
        <div className="comm-table__area">
          <Table
            columns={columns}
            dataSource={data}
            pagination={{
              current: page,
              pageSize: config.pageSize,
            }}
            onRow={record => {
              return {
                onClick: () =>
                  history.push(`/system/school/${record.schoolCode}/edit`),
              };
            }}
            rowClassName="pointer"
          />
        </div>
        <div className="comm-bx__between">
          <div />
          <div className="common-paging center">
            <Pagination
              count={data.length}
              page={page}
              param={params}
              path="/system/school"
            />
          </div>
          <div className="comm-btn__area right">
            {
              <Button
                className="btn-basic pink"
                text={t('common.button.create')}
                onClick={() =>
                  history.push(`/system/school/create?${qs.stringify(params)}`)
                }
                writeAuthYn={writeAuthYn}
              />
            }
          </div>
        </div>
      </div>
      <Footer />

      {confirmDeleteVisible && (
        <ConfirmModal
          visible={confirmDeleteVisible}
          setVisible={setConfirmDeleteVisible}
          onConfirm={onDelete}
          titleText={t('system.menu.title6')}
          messageText={t('system.menu.text3')}
          confirmText={t('common.button.delete')}
          cancelText={t('common.button.cancel')}
        />
      )}
    </div>
    </>
  )
}

export default SystemSchool;